/**
 * Mockup layer order — the single source of truth for how a product mockup
 * is stacked, shared by the creator (CSS/Fabric), the admin preview and the
 * server-side compositor.
 *
 * The stack follows the Shirtigo Mockup Factory: the product colour always
 * sits at the bottom and the cutout mask always clips on top. In between,
 * four orderable layers are stacked bottom → top per template:
 *
 *   heathered → design → base → texture
 *
 * "base" is the mockup image itself. It is a pure shading map (semi-
 * transparent dark pixels along folds and seams), so it belongs ABOVE the
 * design: composited there it darkens the artwork exactly where the garment
 * shows a fold. Below the design it would leave the artwork looking pasted on.
 *
 * Embroidery is the one exception: thread must not be darkened by folds, so
 * the shading drops directly below the design (`shadingBelowDesign`).
 */

export const MOCKUP_LAYER_KEYS = [
  'base',
  'design',
  'heathered',
  'texture',
] as const;

export type MockupLayerKey = (typeof MOCKUP_LAYER_KEYS)[number];

/** Default stack, bottom → top: the base is a shading map above the design. */
export const DEFAULT_MOCKUP_LAYER_ORDER: readonly MockupLayerKey[] = [
  'heathered',
  'design',
  'base',
  'texture',
];

/**
 * Stack for mockups whose base image is a light or opaque rendering inside
 * the print area (mugs, posters, glossy sheens): the design must sit on top
 * of it, otherwise the artwork is veiled or hidden.
 */
export const BASE_BELOW_DESIGN_LAYER_ORDER: readonly MockupLayerKey[] = [
  'base',
  'heathered',
  'design',
  'texture',
];

/**
 * Shirtigo article categories (`art_kat2`, delivered by the catalogue API as
 * `shipping_category`) whose mockup image is a classic photo of the object:
 * the design is composited ON TOP of it. Ported one-to-one from the Shirtigo
 * legacy renderer (MPCMockupProcessor::combineDesignAndTemplateImages):
 * 13 mug, 15 doormat, 17 pillow, 25 printed cap, and the embroidery
 * categories 21, 27, 29, 31 (caps, polos, jackets, bags). Embroidery is
 * additionally applied at render time via the decoration method, so mixed
 * products keep the rule per position.
 */
export const DESIGN_ON_TOP_SHIPPING_CATEGORIES: readonly number[] = [
  13, 15, 17, 21, 25, 27, 29, 31,
];

/**
 * The automatic layer order for a product, decided like Shirtigo does it:
 * by article category, never by looking at the image.
 */
export function mockupLayerOrderForShippingCategory(
  shippingCategory: number | null | undefined
): MockupLayerKey[] {
  return [
    ...(shippingCategory != null &&
    DESIGN_ON_TOP_SHIPPING_CATEGORIES.includes(shippingCategory)
      ? BASE_BELOW_DESIGN_LAYER_ORDER
      : DEFAULT_MOCKUP_LAYER_ORDER),
  ];
}

function isMockupLayerKey(value: string): value is MockupLayerKey {
  return (MOCKUP_LAYER_KEYS as readonly string[]).includes(value);
}

/**
 * Normalise a stored layer order: unknown entries are dropped, missing ones
 * are appended in default order, an empty/missing value yields the default.
 */
export function normalizeMockupLayerOrder(
  order: readonly string[] | null | undefined
): MockupLayerKey[] {
  const known = (order ?? []).filter(isMockupLayerKey);
  const missing = DEFAULT_MOCKUP_LAYER_ORDER.filter(
    (layer) => !known.includes(layer)
  );
  return [...known, ...missing];
}

export interface ResolveMockupLayerOrderOptions {
  /**
   * Put the shading base directly below the design instead of above it.
   * Used for embroidery, where thread must not be darkened by folds.
   */
  shadingBelowDesign?: boolean;
}

/**
 * The effective render order for one mockup: the stored order, normalised,
 * with the embroidery exception applied.
 */
export function resolveMockupLayerOrder(
  order: readonly string[] | null | undefined,
  options: ResolveMockupLayerOrderOptions = {}
): MockupLayerKey[] {
  const normalized = normalizeMockupLayerOrder(order);
  if (!options.shadingBelowDesign) return normalized;

  const baseIndex = normalized.indexOf('base');
  const designIndex = normalized.indexOf('design');
  if (baseIndex < designIndex) return normalized;

  const withoutBase = normalized.filter((layer) => layer !== 'base');
  const insertAt = withoutBase.indexOf('design');
  return [
    ...withoutBase.slice(0, insertAt),
    'base',
    ...withoutBase.slice(insertAt),
  ];
}
